import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Inbox, PenSquare, RefreshCw, Loader2, ExternalLink, Ban, Download, Search, FileText } from 'lucide-react';
import { FOLDER_LABELS, FOLDER_HINTS, statusBubbleClass, statusLabel, filterRows, dealTabUrl, dealOptions, rowOpensInPlace, reviewLabel } from './fundingInboxModel';
import DisclosureReview from '../components/funding-confirmations/DisclosureReview';

// Josh 10/23: "like an inbox style, with drafts and new/compose options — this will route back
// to existing deals". Folders are decided by the API (one classification, shared with the
// deal tab); composing a confirmation is the deal page's Confirmation tab, so "New" = pick the
// deal and go there. Nothing here changes a deal's money.

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
const FOLDER_ORDER = ['drafts', 'awaiting', 'review', 'completed', 'corrections', 'closed'];
const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

function NewConfirmationDialog({ open, onOpenChange, getAuthHeaders }) {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);
  useEffect(() => {
    if (!open) { setQ(''); setResults([]); return undefined; }
    if (q.trim().length < 2) { setResults([]); return undefined; }
    if (timer.current) clearTimeout(timer.current);
    setLoading(true);
    timer.current = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/deals`, { params: { search: q.trim(), limit: 10 }, headers: getAuthHeaders() });
        setResults(dealOptions(r.data));
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Deal search failed');
      } finally { setLoading(false); }
    }, 250);
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [q, open, getAuthHeaders]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>New funding confirmation</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">Pick the deal. The confirmation is composed on that deal's <b>Confirmation</b> tab, where the frozen terms, representative and net proceeds are checked before the link is generated.</p>
        <div className="relative">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
          <Input autoFocus className="pl-8" placeholder="Deal number, business or shortcode…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="fc-new-search" />
        </div>
        <div className="max-h-72 overflow-y-auto divide-y rounded border">
          {loading && <p className="p-3 text-sm text-slate-500"><Loader2 className="inline h-4 w-4 animate-spin mr-1" /> Searching…</p>}
          {!loading && q.trim().length >= 2 && results.length === 0 && <p className="p-3 text-sm text-slate-500">No deals match.</p>}
          {results.map((d) => (
            <button key={d.id} type="button" className="flex w-full items-center justify-between px-3 py-2 text-left hover:bg-slate-50"
              onClick={() => { onOpenChange(false); navigate(`/deals/${d.id}?tab=confirmation`); }} data-testid={`fc-new-pick-${d.deal_id}`}>
              <span><span className="font-medium">#{d.deal_id}</span> <span className="text-slate-700">{d.name}</span></span>
              <Badge variant="outline">{d.status || '—'}</Badge>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function FundingConfirmationInbox() {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const folder = FOLDER_ORDER.includes(searchParams.get('folder')) ? searchParams.get('folder') : 'drafts';
  const [data, setData] = useState({ rows: [], counts: {} });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(null);
  const [composing, setComposing] = useState(false);
  const [open, setOpen] = useState(null);           // Josh 10/28: completed row whose disclosure answers are open in place

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/funding-confirmations`, { params: { folder }, headers: getAuthHeaders() });
      setData(r.data || { rows: [], counts: {} });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load confirmations');
    } finally { setLoading(false); }
  }, [folder, getAuthHeaders]);
  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => filterRows(data.rows, q), [data.rows, q]);
  const pick = (f) => setSearchParams({ folder: f });

  const revoke = async (row) => {
    if (!window.confirm(`Revoke the link for #${row.deal_number} ${row.business_name}? The merchant will no longer be able to open it.`)) return;
    setBusy(row.id);
    try {
      await axios.post(`${API}/funding-confirmations/records/${row.id}/revoke`, {}, { headers: getAuthHeaders() });
      toast.success('Link revoked'); await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Revoke failed'); } finally { setBusy(null); }
  };
  const attachReceipt = async (row) => {
    setBusy(row.id);
    try {
      const r = await axios.post(`${API}/funding-confirmations/records/${row.id}/attach-receipt`, {}, { headers: getAuthHeaders() });
      toast.success(r.data.attached ? `Signed PDF filed under deal #${row.deal_number}` : 'Already filed under the deal');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not file the PDF'); } finally { setBusy(null); }
  };
  // Josh 10/28: the staff decision on a flagged completion, recorded right on the row.
  const decide = async (row, decision, note) => {
    setBusy(row.id);
    try {
      const r = await axios.post(`${API}/funding-confirmations/records/${row.id}/review`, { decision, note }, { headers: getAuthHeaders() });
      toast.success(`${reviewLabel(r.data.review)} — #${row.deal_number} ${row.business_name}`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not record the review'); } finally { setBusy(null); }
  };
  const exportRecord = async (row) => {
    setBusy(row.id);
    try {
      const r = await axios.get(`${API}/funding-confirmations/records/${row.id}/evidence`, { headers: getAuthHeaders(), responseType: 'blob' });
      const url = URL.createObjectURL(r.data); const a = document.createElement('a'); a.href = url; a.download = `confirmation-${row.id}.json`; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.response?.data?.detail || 'Export failed'); } finally { setBusy(null); }
  };

  return (
    <Layout>
      <div className="flex flex-col gap-4 md:flex-row" data-testid="funding-confirmation-inbox">
        {/* Folders */}
        <aside className="md:w-64 shrink-0 space-y-3">
          <Button className="w-full" onClick={() => setComposing(true)} data-testid="fc-new"><PenSquare className="h-4 w-4 mr-2" /> New confirmation</Button>
          <nav className="rounded-lg border bg-white divide-y">
            {FOLDER_ORDER.map((f) => (
              <button key={f} type="button" onClick={() => pick(f)}
                className={`flex w-full items-center justify-between px-3 py-2 text-sm ${folder === f ? 'bg-blue-50 font-semibold text-blue-900' : 'hover:bg-slate-50'}`}
                data-testid={`fc-folder-${f}`}>
                <span>{FOLDER_LABELS[f]}</span>
                <span className={`rounded-full px-2 text-xs ${data.counts?.[f] ? 'bg-slate-200 text-slate-800' : 'text-slate-400'}`}>{data.counts?.[f] ?? 0}</span>
              </button>
            ))}
          </nav>
          <p className="text-xs text-slate-500 px-1">{FOLDER_HINTS[folder]}</p>
        </aside>

        {/* List */}
        <section className="flex-1 min-w-0">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h1 className="text-xl font-semibold flex items-center gap-2"><Inbox className="h-5 w-5 text-blue-700" /> Funding confirmation · {FOLDER_LABELS[folder]}</h1>
            <div className="flex items-center gap-2">
              <Input placeholder="Search business, deal #, signer…" value={q} onChange={(e) => setQ(e.target.value)} className="w-64" data-testid="fc-search" />
              <Button variant="outline" onClick={load} disabled={loading}><RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /></Button>
            </div>
          </div>
          <div className="rounded-lg border bg-white divide-y">
            {loading ? <p className="p-6 text-sm text-slate-500"><Loader2 className="inline h-4 w-4 animate-spin mr-1" /> Loading…</p>
              : rows.length === 0 ? <p className="p-6 text-sm text-slate-500">Nothing in {FOLDER_LABELS[folder]}.</p>
              : rows.map((row) => (
                <div key={row.id} className="px-4 py-3 hover:bg-slate-50" data-testid={`fc-row-${row.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <button type="button" className="min-w-0 flex-1 text-left" title={rowOpensInPlace(row) ? 'Show the disclosure answers' : "Open the deal's Confirmation tab"}
                    onClick={() => (rowOpensInPlace(row) ? setOpen(open === row.id ? null : row.id) : navigate(dealTabUrl(row)))} data-testid={`fc-open-${row.id}`}>
                    <p className="font-medium truncate">#{row.deal_number} · {row.business_name}</p>
                    <p className="text-xs text-slate-500 truncate">{row.company} · {row.signer_name}{row.signer_title ? `, ${row.signer_title}` : ''}
                      <span className={`ml-2 ${statusBubbleClass(row.status)}`} data-testid={`fc-status-${row.id}`}>{statusLabel(row.status)}</span>
                      {row.status === 'completed' && row.receipt_document_id && <span className="ml-2 text-emerald-700">✓ PDF in deal Documents</span>}
                      {row.requires_review && !row.review && <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-amber-900">Staff review</span>}
                      {row.review && <span className={`ml-2 rounded px-1.5 py-0.5 ${row.review.decision === 'cleared' ? 'bg-emerald-100 text-emerald-900' : 'bg-red-100 text-red-900'}`}>{reviewLabel(row.review)}</span>}</p>
                    <p className="text-xs text-slate-400">Issued {fmt(row.created_at)}{row.completed_at ? ` · signed ${fmt(row.completed_at)}` : row.expires_at ? ` · expires ${fmt(row.expires_at)}` : ''}</p>
                  </button>
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="ghost" title="Open the deal's Confirmation tab" onClick={() => navigate(dealTabUrl(row))}><ExternalLink className="h-4 w-4" /></Button>
                    {['pending', 'verified'].includes(row.status) && folder !== 'closed' && (
                      <Button size="sm" variant="ghost" title="Revoke link" disabled={busy === row.id} onClick={() => revoke(row)}><Ban className="h-4 w-4 text-red-600" /></Button>
                    )}
                    {row.status === 'completed' && (
                      <Button size="sm" variant="ghost" title="Export signed evidence (JSON)" disabled={busy === row.id} onClick={() => exportRecord(row)}><Download className="h-4 w-4" /></Button>
                    )}
                    {row.status === 'completed' && !row.receipt_document_id && (
                      <Button size="sm" variant="ghost" title="File the signed PDF under the deal's Documents" disabled={busy === row.id} onClick={() => attachReceipt(row)}><FileText className="h-4 w-4" /></Button>
                    )}
                  </div>
                </div>
                {open === row.id && rowOpensInPlace(row) && (
                  <div className="mt-3"><DisclosureReview record={row} busy={busy === row.id} onDecide={(decision, note) => decide(row, decision, note)} /></div>
                )}
                </div>
              ))}
          </div>
          <p className="mt-3 text-xs text-slate-500">A confirmation never funds a deal or changes its ledger. Evidence exports contain identity details — store them securely.</p>
        </section>
      </div>
      <NewConfirmationDialog open={composing} onOpenChange={setComposing} getAuthHeaders={getAuthHeaders} />
    </Layout>
  );
}
